import React, { useState } from 'react';
import { PATTERNS, SELECTABLE_PATTERNS, PATTERN_LABEL } from './pagePatterns';

/**
 * PagePatternFloat — 미리보기 용지 위에 뜨는 투명 컨트롤(면 배열 선택).
 *
 * 설계 제약(작업지시서 §4):
 *  - `.pv4-page` 안에 position:absolute로 띄운다. 레이아웃 공간을 차지하지 않으므로
 *    인쇄에서 `.pv4-no-print`로 사라져도 페이지 나눔이 바뀌지 않는다.
 *  - 축소 배율(zoom 0.2~1.2) 보정은 `transform: scale`로 한다. 컨트롤에 zoom을 또 걸면
 *    절대배치 요소의 top/left 해석이 중첩돼 브라우저별로 갈린다.
 *  - hover 판정은 컴포넌트 안 React 상태로 한다. 용지(.pv4-page)를 대상으로 하려면 CSS가
 *    필요한데, 그 CSS를 넣을 sheetChromeCss는 측정용 오프스크린 렌더에도 주입되는 공용 소스다.
 */

const BAR_BG = 'rgba(17, 24, 39, 0.52)';

function PatternGlyph({ pattern }) {
  const def = PATTERNS[pattern];
  const column = (x, kind) => {
    if (kind === 'f') return <rect x={x} y="2" width="7" height="10" rx="1.4" />;
    return (
      <React.Fragment>
        <rect x={x} y="2" width="7" height="4.4" rx="1.4" />
        <rect x={x} y="7.6" width="7" height="4.4" rx="1.4" />
      </React.Fragment>
    );
  };
  return (
    <svg viewBox="0 0 18 14" width="17" height="13" fill="currentColor" aria-hidden="true">
      {column(1.5, def.left)}
      {def.right ? column(9.5, def.right) : null}
    </svg>
  );
}

/** want → actual 로 물러난 경우의 표시. 문항 수가 유지되면 알림(회색), 줄었으면 경고(주황) */
function fallbackBadge(want, actual, overflowNo) {
  if (overflowNo) return { text: `⚠ ${overflowNo}번 넘침`, bg: 'rgba(185,28,28,0.9)' };
  if (!want || want === 'auto' || want === 'BC' || !actual || actual === want) return null;
  const wantCount = PATTERNS[want] ? PATTERNS[want].count : null;
  const actualCount = PATTERNS[actual] ? PATTERNS[actual].count : null;
  const sameCount = wantCount != null && wantCount === actualCount;
  return {
    text: sameCount ? `${want}→${actual}` : `${want}→${actual} ⚠`,
    bg: sameCount ? 'rgba(55,65,81,0.8)' : 'rgba(180,83,9,0.88)',
  };
}

function PagePatternFloat({ pageIndex, zoom, current, want, overflowNo, pinned, onPick, onReset }) {
  const [hot, setHot] = useState(false);
  // 화면상 크기를 배율과 무관하게 유지하되, 아주 축소했을 때 용지 폭을 넘지 않도록 2.5배로 묶는다
  const scale = Math.min(Math.max(1 / (zoom || 1), 1), 2.5);
  const badge = fallbackBadge(want, current, overflowNo);

  const btn = {
    width: 35, height: 20, padding: 0, border: 'none', background: 'transparent',
    color: '#e5e7eb', borderRadius: 999, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3, lineHeight: 1,
  };
  const btnOn = { background: '#fff', color: '#0f172a' };

  return (
    <div
      className="pv4-no-print"
      style={{ position: 'absolute', top: '2mm', left: '50%', transform: 'translateX(-50%)', zIndex: 6 }}
    >
      <div style={{ transform: `scale(${scale})`, transformOrigin: 'top center' }}>
        <div
          onMouseEnter={() => setHot(true)}
          onMouseLeave={() => setHot(false)}
          onFocus={() => setHot(true)}
          onBlur={() => setHot(false)}
          style={{
            display: 'flex', alignItems: 'center', gap: 2, padding: '3px 6px',
            borderRadius: 999, background: BAR_BG,
            WebkitBackdropFilter: 'blur(6px)', backdropFilter: 'blur(6px)',
            opacity: hot ? 1 : 0.45, transition: 'opacity .15s ease',
            fontFamily: "'Pretendard', sans-serif", whiteSpace: 'nowrap',
          }}
        >
          <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', padding: '0 3px' }}>{pageIndex + 1}면</span>
          <span style={{ width: 1, alignSelf: 'stretch', margin: '3px 2px', background: 'rgba(255,255,255,0.22)' }} />
          <button
            type="button"
            onClick={onReset}
            title="이 면의 지정을 지우고 기본값으로"
            aria-label="이 면의 지정 초기화"
            style={{ ...btn, width: 22, fontSize: 13, color: pinned ? '#fff' : 'rgba(229,231,235,0.5)' }}
          >
            ⟲
          </button>
          <span style={{ width: 1, alignSelf: 'stretch', margin: '3px 2px', background: 'rgba(255,255,255,0.22)' }} />
          {SELECTABLE_PATTERNS.map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => onPick(key)}
              title={PATTERN_LABEL[key]}
              aria-label={PATTERN_LABEL[key]}
              aria-pressed={current === key}
              style={current === key ? { ...btn, ...btnOn } : btn}
            >
              {/* 글자(A·B·C·D)가 이름, 글리프가 뜻 — 둘을 같이 보여야 어떤 배열인지 바로 읽힌다 */}
              <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '-0.02em' }}>{key}</span>
              <PatternGlyph pattern={key} />
            </button>
          ))}
          {badge && (
            <span
              title={overflowNo
                ? `${overflowNo}번이 단 높이를 초과해 잘릴 수 있습니다`
                : `고른 배열(${want})에 들어가지 않아 ${current}로 배치했습니다`}
              style={{
                marginLeft: 3, padding: '2px 7px', borderRadius: 999, background: badge.bg,
                color: '#fff', fontSize: 10, fontWeight: 700, fontFamily: "'Pretendard', sans-serif",
              }}
            >
              {badge.text}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default PagePatternFloat;
